import { RootState } from '@next/core-lib/app';
import { DataSeries } from '@next/core-lib/models';
import { SettingsDataObject } from '@next/core-lib/settings';
import { EntityState } from '@ngrx/entity';

export interface RefValSheetModuleState extends RootState {
  refValSheets?: RefValSheetState;
  baseRefValSheet?: ReferenceValueSheet;
  selectedCountry?: string;
  isLoaded: boolean;
  columns: string[];
}

export type RefValSheetState = EntityState<ReferenceValueSheet>;

export interface ReferenceValueSheet extends SettingsDataObject {
  id: number;
  locationId: number;
  updatedAdvisorProfileId: number;
  createdAdvisorProfileId: number;
  updatedAdvisorName: string;
  createdAdvisorName: string;
  name: string;
  description: string;
  isActive: boolean;
  updated: string;
  created: string;
  country?: string;
  isStandardReferenceValueSheet: boolean;
  standardReferenceValueSheetId?: number;
  series: ReferenceValueSheetSeries;
  referenceValueSheetRows: ReferenceValueSheetRow[];
  temperatureUnit: TemperatureUnit;
  feedUnit: FeedUnit;
  waterUnit: WaterUnit;
  weightUnit: WeightUnit;
}

export interface ReferenceValueSheetSeries extends SettingsDataObject {
  temperatureInside: boolean;
  relativeHumidity: boolean;
  feedIntakePerChickPerDay: boolean;
  feedIntakePerChickTotal: boolean;
  waterIntake: boolean;
  weight: boolean;
  mortality: boolean;
  feedConversion: boolean;
  waterToFeedRatio: boolean;
}

export interface ReferenceValueSheetRow extends SettingsDataObject {
  id: number;
  day: number;
  temperatureInside?: number;
  relativeHumidity?: number;
  feedIntakePerChickPerDay?: number;
  feedIntakePerChickTotal?: number;
  waterIntake?: number;
  weight?: number;
  mortality?: number;
  feedConversion?: number;
  waterToFeedRatio?: number;
}

export interface GetReferenceValueSheetsResponse {
  referenceValueSheets: ReferenceValueSheet[];
}

export interface UpsertRefValSheetResponse {
  id?: number;
}

export enum TemperatureUnit {
  Celcius = 'Celcius',
  Fahrenheit = 'Fahrenheit',
}

export enum FeedUnit {
  Gram = 'Gram',
  Kilogram = 'Kilogram',
}

export enum WaterUnit {
  ML = 'ML',
  DL = 'DL',
  L = 'L',
}

export enum WeightUnit {
  Gram = 'Gram',
  Kilogram = 'Kilogram',
}

export enum FeedIntakeUnit {
  CumulativeKilogram = 'CumulativeKilogram',
  GramPerBird = 'GramPerBird',
}

export enum WaterIntakeUnit {
  CumulativeLiter = 'CumulativeLiter',
  MililiterPerBird = 'MililiterPerBird',
}

export class ColumnSettings {
  public static readonly DAY = 'day';
  public static readonly TEMPERATURE_INSIDE = 'temperatureInside';
  public static readonly RELATIVE_HUMIDITY = 'relativeHumidity';
  public static readonly FEED_INTAKE_PER_CHICK_PER_DAY = 'feedIntakePerChickPerDay';
  public static readonly FEED_INTAKE_PER_CHICK_TOTAL = 'feedIntakePerChickTotal';
  public static readonly WATER_INTAKE = 'waterIntake';
  public static readonly WEIGHT = 'weight';
  public static readonly MORTALITY = 'mortality';
  public static readonly FEED_CONVERSION = 'feedConversion';
  public static readonly WATER_TO_FEED_RATIO = 'waterToFeedRatio';
  public static readonly DELETE_ROW = 'deleteRow';
  public static readonly COLUMN_SETTINGS_ARRAY_DEFAULT = [
    ColumnSettings.DAY,
    ColumnSettings.TEMPERATURE_INSIDE,
    ColumnSettings.RELATIVE_HUMIDITY,
    ColumnSettings.FEED_INTAKE_PER_CHICK_PER_DAY,
    ColumnSettings.FEED_INTAKE_PER_CHICK_TOTAL,
    ColumnSettings.WATER_INTAKE,
    ColumnSettings.WEIGHT,
    ColumnSettings.MORTALITY,
    ColumnSettings.FEED_CONVERSION,
    ColumnSettings.WATER_TO_FEED_RATIO,
  ];
}

export interface ReferenceValueSheetDataSeriesResponse {
  series: DataSeries<number, number>;
}

export enum AnimalType {
  BROILER = 'Broiler',
  REARING = 'Rearing',
}
